import _ from 'underscore';
import React, { useContext, useMemo, useCallback, useRef, Suspense } from 'react';
import PropTypes from 'prop-types';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { HelpRailViewId } from '@bingads-webui-react/help-rail-view-id';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PerformanceMarkerProvider, PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { AcceptInvitationPage } from '@bingads-webui-campaign-react/accept-invitation-page';
import { PmaxLiteSignupAppCore } from './components/pmax-lite-signup-app-core';
import { SupportModalsForSignup } from './support-modal';
import { isAcceptInvitationRoute } from './route-utils';

// Renders the post-auth accept-invitation page and completes the surrounding
// `ShellPage_PmaxLiteSignup_AcceptInvitation` perf marker when the page signals its first
// meaningful screen is ready (Validate + ActivationOptions resolved). The invitation page bypasses
// PmaxLiteSignupAppCore (and its `ShellPage_PmaxLiteSignupUI` marker), so without this it would emit
// no page-render marker. `done()` is fired once (ref-guarded); the context marker is null outside a
// provider (defensive guard for tests).
const AcceptInvitationPageWithPerfMarker = () => {
  const perfMarker = useContext(PerformanceMarkerContext);
  const isDoneRef = useRef(false);
  const handleReady = useCallback(() => {
    if (isDoneRef.current || !perfMarker) {
      return;
    }
    isDoneRef.current = true;
    perfMarker.done();
  }, [perfMarker]);
  return <AcceptInvitationPage onReady={handleReady} />;
};

export const AppCore = ({ i18nObjects, devConfig }) => {
  const serverContext = useContext(ServerContext);
  const instrumentation = useContext(InstrumentationContext);

  const ccUIPilots = useMemo(() => (
    (serverContext && serverContext.CCUIPilots) || {}
  ), [serverContext]);

  const dynamicCCUIPilots = useMemo(() => (
    (serverContext && serverContext.CCUIPilots && serverContext.CCUIPilots.dynamic) || {}
  ), [serverContext]);

  const permissions = useMemo(() => (
    (serverContext && serverContext.Permissions) || {}
  ), [serverContext]);

  // When customer is created, the pilot in serverContext.CCUIPilots.dynamic will be updated,
  // such as ABLCanadaTax. So get the serverContext from Context to get the latest permissions.
  const allPermissions = useMemo(() => (
    _.extend(
      {},
      permissions,
      ccUIPilots,
      dynamicCCUIPilots
    )
  ), [ccUIPilots, dynamicCCUIPilots, permissions]);

  return (
    <PermissionsContext.Provider value={allPermissions} >
      <HelpRailViewId
        railViewId="Campaign_signup?option=2"
      />
      {isAcceptInvitationRoute()
        ? (
          <PerformanceMarkerProvider
            name="ShellPage_PmaxLiteSignup_AcceptInvitation"
            instrumentation={instrumentation}
            immediateDone={false}
          >
            <AcceptInvitationPageWithPerfMarker />
          </PerformanceMarkerProvider>
        )
        : <PmaxLiteSignupAppCore devConfig={devConfig} />}
      <Suspense fallback={<div />}>
        <SupportModalsForSignup
          i18nObjects={i18nObjects}
        />
      </Suspense>
    </PermissionsContext.Provider>
  );
};

AppCore.propTypes = {
  i18nObjects: PropTypes.shape({
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }),
    legacyI18n: PropTypes.shape({
      getString: PropTypes.func,
    }),
  }).isRequired,
  devConfig: PropTypes.shape({
    isInDevelopmentMode: PropTypes.bool.isRequired,
  }).isRequired,
};

